import { Agent, type Dispatcher } from "undici";
import type { SiteAdapter, AdapterContext } from "../adapters/base.js";
import type { HttpClient, HttpEvent } from "../http/types.js";
import type { Logger } from "../utils/logger.js";
import type { NovelMetadata, ChapterRef } from "../data-model/novel.js";
import type { DownloadOptions, CacheOptions, TransportConfig, TransportMode, RateLimitOptions, RetryOptions } from "../data-model/options.js";
import type { DownloadResult } from "../data-model/result.js";
import type { Url, AdapterId } from "../data-model/primitives.js";
import type { BrowserClient, BrowserModule } from "../http/browser/types.js";
import type { CookieCaptureResult } from "../http/tiered.js";
import { AdapterRegistry } from "./registry.js";
import { EventBus } from "./events.js";
import { buildHttpClient, normalizeTransport } from "../http/index.js";
import { CookieJar } from "../http/cookie-jar.js";
import { BrowserPool } from "../http/browser/pool.js";
import { BrowserClientImpl } from "../http/browser/client.js";
import { safeClosePage } from "../http/browser/safe-close.js";
import { createLogger, silentLogger, type LogLevel } from "../utils/logger.js";
import type { DownloadEvent } from "../data-model/events.js";
import { parseUrl } from "../data-model/parse.js";
import { fetchMetadataStage } from "../pipeline/fetch-metadata.js";
import { fetchTocStage } from "../pipeline/fetch-toc.js";
import type { FetchTocRange } from "../pipeline/fetch-toc.js";
import { ParseError, normalizeToEnvelopeError } from "./errors.js";
import { FilesystemCacheStore } from "../storage/cache-store.js";
import { HttpCacheClient } from "../storage/http-cache.js";
import { DownloadRun, normalizeChapterRange } from "./download-run.js";
import { resolveResumeStorage } from "../storage/resume-storage.js";

const DEFAULT_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_CONCURRENCY = 4;

/**
 * Orchestrates a download: resolves a site adapter from the URL, fetches
 * metadata + the table of contents, then fetches chapters concurrently with
 * shared rate-limiting, retries, optional caching, and resumable state.
 *
 * Most callers use the shared `downloader` singleton or `createDownloader()`
 * rather than constructing this directly. An instance owns a per-host transport
 * (and may hold a browser pool), so call {@link Downloader.dispose} when done.
 */
export class Downloader {
  readonly #registry: AdapterRegistry;
  readonly #http: HttpClient;
  readonly #logger: Logger;
  readonly #transport: TransportConfig;
  readonly #cookieJar: CookieJar;
  readonly #browserPool: BrowserPool | undefined;
  readonly #httpDispatcher: Dispatcher | undefined;
  readonly #transportListeners = new Set<(e: DownloadEvent) => void>();

  /**
   * Build a downloader from a set of adapters. `rateLimit`, `retry`, and
   * `transport` configure the shared per-host transport (set once here, not
   * per call). Provide `http` to replace the entire transport stack with a
   * custom {@link HttpClient}. Prefer `createDownloader()` for the built-in
   * adapters; use this constructor for a custom adapter set or transport.
   */
  constructor(opts: {
    adapters: readonly SiteAdapter[];
    http?: HttpClient;
    logger?: Logger;
    transport?: TransportConfig | TransportMode;
    rateLimit?: RateLimitOptions;
    retry?: RetryOptions;
    /** @internal — test seam for browser module loading */
    browserModuleLoader?: () => Promise<BrowserModule>;
    /** @internal — test seam: replaces the real UndiciHttpClient leaf */
    undiciOverride?: HttpClient;
    logLevel?: LogLevel;
  }) {
    this.#registry = new AdapterRegistry(opts.adapters);
    this.#logger =
      opts.logger ??
      (opts.logLevel !== undefined ? createLogger({ level: opts.logLevel }) : silentLogger);
    this.#transport = normalizeTransport(opts.transport);
    this.#cookieJar = new CookieJar();

    if (this.#transport.mode !== "http-only") {
      const browserOpts = this.#transport.browserOptions;
      this.#browserPool = new BrowserPool({
        logger: this.#logger,
        ...(opts.browserModuleLoader !== undefined ? { moduleLoader: opts.browserModuleLoader } : {}),
        ...(browserOpts?.idleTimeoutMs !== undefined ? { idleTimeoutMs: browserOpts.idleTimeoutMs } : {}),
        ...(browserOpts?.headed !== undefined ? { headed: browserOpts.headed } : {}),
        ...(browserOpts?.executablePath !== undefined ? { executablePath: browserOpts.executablePath } : {}),
      });
    }

    const pool = this.#browserPool;

    const browserBranchFactory = pool !== undefined ? this.#buildBrowserBranchFactory(pool) : undefined;

    const onCookieCapture: ((host: string) => Promise<CookieCaptureResult>) | undefined = pool !== undefined
      ? async (host): Promise<CookieCaptureResult> => {
          const handle = await pool.acquireContext();
          try {
            const { captureCfCookies } = await import("../http/browser/client.js");
            return await captureCfCookies(handle.context, `https://${host}`);
          } finally {
            await handle.release();
          }
        }
      : undefined;

    // Own the leaf dispatcher so requests never route through the process-global
    // one (immune to another dependency's undici version) and its sockets are
    // released in dispose(). Skipped when the whole transport is provided.
    this.#httpDispatcher =
      opts.http === undefined && opts.undiciOverride === undefined ? new Agent() : undefined;

    this.#http = opts.http ?? buildHttpClient({
      logger: this.#logger,
      transport: this.#transport,
      cookieJar: this.#cookieJar,
      ...(this.#httpDispatcher !== undefined ? { dispatcher: this.#httpDispatcher } : {}),
      ...(opts.rateLimit !== undefined ? { rateLimit: opts.rateLimit } : {}),
      ...(opts.retry !== undefined ? { retry: opts.retry } : {}),
      onEvent: (e: HttpEvent): void => {
        if (e.type === "rate-limit:wait") {
          const out: DownloadEvent = { type: "rate-limit:wait", host: e.host, waitMs: e.waitMs };
          for (const listener of this.#transportListeners) listener(out);
        } else if (e.type === "http:retry") {
          const out: DownloadEvent = {
            type: "http:retry",
            url: e.url,
            attempt: e.attempt,
            waitMs: e.waitMs,
            ...(e.status !== undefined ? { status: e.status } : {}),
          };
          for (const listener of this.#transportListeners) listener(out);
        }
      },
      ...(opts.undiciOverride !== undefined ? { undiciOverride: opts.undiciOverride } : {}),
      preferredTransport: (url) => {
        try {
          const adapter = this.#registry.resolve(String(url));
          return adapter.preferredTransport === "browser" ? "browser" : "http";
        } catch {
          return "http";
        }
      },
      ...(browserBranchFactory !== undefined ? { browserBranchFactory } : {}),
      ...(onCookieCapture !== undefined ? { onCookieCapture } : {}),
    });
  }

  /** Release held resources (closes the browser pool and HTTP dispatcher, if any). Safe to call once when done. */
  async dispose(): Promise<void> {
    const pool = this.#browserPool;
    if (pool) await pool.dispose();
    if (this.#httpDispatcher) await this.#httpDispatcher.close();
  }

  /** True when a built-in/registered adapter can handle this URL. */
  canHandle(url: string): boolean {
    return this.#registry.tryResolve(url) !== null;
  }

  /** Plain, serializable list of the sites this instance can download. */
  supportedSites(): readonly { id: AdapterId; displayName: string; hostnames: readonly string[] }[] {
    return this.#registry.list().map((a) => ({
      id: a.id,
      displayName: a.displayName,
      hostnames: a.hostnames,
    }));
  }

  /** @internal — for tests only. Returns a ready ctx and a release fn. */
  async _ctxFor(
    adapter: SiteAdapter,
    signal: AbortSignal,
  ): Promise<{ ctx: AdapterContext; release: () => Promise<void> }> {
    const handle = await this.#maybeBuildBrowserClient(adapter);
    const ctx: AdapterContext = {
      http: this.#http,
      logger: this.#logger,
      signal,
      ...(handle !== null ? { browser: handle.client } : {}),
    };
    return {
      ctx,
      release: handle !== null ? handle.release : async () => {},
    };
  }

  /**
   * Low-level single-stage metadata fetch. Unlike `download()`, this returns a
   * bare value and therefore **throws** on failure (`AdapterNotFoundError`,
   * `ParseError`, or any `DownloadError`). Use `download()` if you want the
   * `{ status: "error" }` envelope instead of exceptions.
   */
  async fetchMetadata(url: string, options?: DownloadOptions): Promise<NovelMetadata> {
    const adapter: SiteAdapter = options?.adapter ?? this.#registry.resolve(url);
    const validated = parseUrl(url);
    const bus = new EventBus(options?.onEvent, this.#logger);
    const transportListener = (e: DownloadEvent): void => bus.emit(e);
    const browserClient = await this.#maybeBuildBrowserClient(adapter);
    const ctx: AdapterContext = {
      http: this.#http,
      logger: this.#logger,
      signal: options?.signal ?? new AbortController().signal,
      ...(browserClient !== null ? { browser: browserClient.client } : {}),
    };
    try {
      this.#transportListeners.add(transportListener);
      return await fetchMetadataStage(adapter, validated, ctx, bus);
    } finally {
      this.#transportListeners.delete(transportListener);
      if (browserClient !== null) await browserClient.release();
    }
  }

  /**
   * Low-level table-of-contents fetch: returns the chapter list (refs only —
   * `index`, `title`, `url`) **without downloading any chapter bodies**,
   * honoring `options.chapterRange`. This is the cheap "list-only" counterpart
   * to `download()`. Like {@link fetchMetadata} it returns a bare value and
   * therefore **throws** on failure (`AdapterNotFoundError`, `ParseError`, or
   * any `DownloadError`); use `download()` for the full novel with bodies.
   */
  async fetchChapterList(url: string, options?: DownloadOptions): Promise<readonly ChapterRef[]> {
    const adapter: SiteAdapter = options?.adapter ?? this.#registry.resolve(url);
    const validated = parseUrl(url);
    const range = normalizeChapterRange(options?.chapterRange);
    const bus = new EventBus(options?.onEvent, this.#logger);
    const transportListener = (e: DownloadEvent): void => bus.emit(e);
    const browserClient = await this.#maybeBuildBrowserClient(adapter);
    const ctx: AdapterContext = {
      http: this.#http,
      logger: this.#logger,
      signal: options?.signal ?? new AbortController().signal,
      ...(browserClient !== null ? { browser: browserClient.client } : {}),
    };
    const refs: ChapterRef[] = [];
    try {
      this.#transportListeners.add(transportListener);
      await fetchTocStage(adapter, validated, ctx, bus, (ref) => {
        refs.push(ref);
      }, range);
      return refs;
    } finally {
      this.#transportListeners.delete(transportListener);
      if (browserClient !== null) await browserClient.release();
    }
  }

  /**
   * Download a novel end to end. Returns a {@link DownloadResult} envelope —
   * `"success"`, `"partial"` (some chapters failed; carries a `resumeToken`
   * when `options.resume` is set), or `"error"` (including an unsupported or
   * malformed URL). The only thrown error is `CancelledError`, when
   * `options.signal` aborts. Use `options.onEvent` for progress.
   */
  async download(url: string, options?: DownloadOptions): Promise<DownloadResult> {
    let adapter: SiteAdapter;
    let validated: Url;
    let normalizedRange: FetchTocRange | undefined;
    try {
      validated = parseUrl(url);
      adapter = options?.adapter ?? this.#registry.resolve(url);
      normalizedRange = normalizeChapterRange(options?.chapterRange);
    } catch (err) {
      return {
        status: "error",
        error: normalizeToEnvelopeError(err),
      };
    }
    const bus = new EventBus(options?.onEvent, this.#logger);
    const transportListener = (e: DownloadEvent): void => bus.emit(e);
    const concurrency = options?.concurrency ?? DEFAULT_CONCURRENCY;
    const signal = options?.signal ?? new AbortController().signal;

    const browserClient = await this.#maybeBuildBrowserClient(adapter);
    const httpForDownload = this.#wrapHttpForDownload(options?.cache, bus);
    const ctx: AdapterContext = {
      http: httpForDownload,
      logger: this.#logger,
      signal,
      ...(browserClient !== null ? { browser: browserClient.client } : {}),
    };

    try {
      this.#transportListeners.add(transportListener);
      const resumeStorage = resolveResumeStorage(options?.resume, validated, this.#logger);
      const run = new DownloadRun({
        adapter,
        validated,
        normalizedRange,
        resumeStorage,
        concurrency,
        signal,
        ctx,
        bus,
        logger: this.#logger,
      });
      return await run.execute();
    } finally {
      this.#transportListeners.delete(transportListener);
      if (browserClient !== null) await browserClient.release();
    }
  }

  #buildBrowserBranchFactory(pool: BrowserPool): () => HttpClient {
    return () => ({
      async get(
        url: Url,
        reqOpts?: import("../http/types.js").HttpRequestOptions,
      ): Promise<import("../http/types.js").HttpResponse> {
        const handle = await pool.acquireContext();
        try {
          const page = await handle.context.newPage();
          try {
            const response = await page.goto(String(url), {
              timeout: 30_000,
              waitUntil: "domcontentloaded",
              ...(reqOpts?.signal ? { signal: reqOpts.signal } : {}),
            });
            const body = await page.content();
            const status = response?.status() ?? 200;
            const headers = response?.headers() ?? {};
            return { status, headers, body, url };
          } finally {
            await safeClosePage(page);
          }
        } finally {
          await handle.release();
        }
      },
    });
  }

  async #maybeBuildBrowserClient(
    adapter: SiteAdapter,
  ): Promise<{ client: BrowserClient; release: () => Promise<void> } | null> {
    const pool = this.#browserPool;
    if (!pool) {
      if (adapter.preferredTransport === "browser" && this.#transport.mode === "http-only") {
        throw new ParseError(
          `Adapter ${adapter.id} requires the browser tier; current transport mode is "http-only".`,
        );
      }
      return null;
    }
    if (adapter.preferredTransport !== "browser" && this.#transport.mode !== "browser-required") {
      return null;
    }
    const handle = await pool.acquireContext();
    const navTimeout = this.#transport.browserOptions?.navigationTimeoutMs;
    const client = new BrowserClientImpl({
      context: handle.context,
      ...(navTimeout !== undefined ? { navigationTimeoutMs: navTimeout } : {}),
    });
    return { client, release: handle.release };
  }

  #wrapHttpForDownload(cache: CacheOptions | undefined, bus: EventBus): HttpClient {
    if (!cache) return this.#http;
    const store = new FilesystemCacheStore(cache.dir);
    const onEvent = (e: HttpEvent): void => {
      if (e.type === "cache:hit") bus.emit({ type: "cache:hit", url: e.url });
    };
    return new HttpCacheClient(this.#http, store, {
      maxAgeMs: cache.maxAgeMs ?? DEFAULT_CACHE_MAX_AGE_MS,
      onEvent,
    });
  }
}
